import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, MessageSquare, Clapperboard, Zap, Shield, Sparkles, Check, X, Lock, Star, Globe, Mic, Image as ImageIcon, GraduationCap, Cloud } from "lucide-react";
import Logo from "@/components/Logo";
import LangToggle from "@/components/LangToggle";
import PWAInstallButton from "@/components/PWAInstallButton";
import FreeTrialBadge from "@/components/FreeTrialBadge";
import TrialRibbon from "@/components/TrialRibbon";
import TrialPrimaryCTA from "@/components/TrialPrimaryCTA";
import VideoDemo from "@/components/VideoDemo";
import DownloadSection from "@/components/DownloadSection";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import useSEO from "@/hooks/useSEO";
import { T } from "@/components/InlineSiteCopyEditor";

export default function Landing() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [flags, setFlags] = useState({});

  useSEO({
    title: "Lumioos — All-in-one AI Workspace for Creators & Freelancers",
    description: "GPT-5, Claude Opus 4.5, Gemini 3, Sora 2 video, image gen, CV builder and interview practice — one subscription, 14-day free trial.",
    canonical: "https://mylumioos.com/",
  });

  // Auto-redirect signed-in users to the app dashboard. Important for the
  // iOS / Android native shell where a successful `POST /auth/google-mobile`
  // stores the session in localStorage but then keeps the WKWebView on `/`.
  // Without this, the user lands back on the marketing page after login
  // and has no obvious next step. Runs ONLY when AuthContext has finished
  // hydrating (`user` truthy) so we don't fire during the initial render
  // while the token is still being validated. Admins are exempt from this
  // bounce so they can view + inline-edit the landing copy in place.
  useEffect(() => {
    if (user && user.id && !user.is_admin) {
      navigate("/app", { replace: true });
    }
  }, [user, navigate]);

  // Safety net for the iOS WKWebView flow: if the native app drops us
  // here with `?token=<JWT>` (or `#token=<JWT>`) in the URL — meaning
  // it hasn't been updated to use the new /api/auth/mobile-bridge
  // endpoint — we still pick the token up and store it in localStorage,
  // then bounce to /app. This makes the bridge-less old iOS build work
  // out of the box too, without waiting for an iOS rebuild.
  useEffect(() => {
    if (user && user.id) return;  // already signed in via stored token
    try {
      const url = new URL(window.location.href);
      // accept token in either ?token=, ?session=, ?jwt=, or #token=…
      let token =
        url.searchParams.get("token") ||
        url.searchParams.get("session") ||
        url.searchParams.get("jwt") ||
        "";
      if (!token && url.hash) {
        const h = new URLSearchParams(url.hash.replace(/^#/, ""));
        token = h.get("token") || h.get("session") || h.get("jwt") || "";
      }
      if (token && token.length > 20) {
        localStorage.setItem("smartai_token", token);
        // Replace history so the token never appears in the back-stack
        // (also strips it from Referer for any subsequent navigation).
        window.location.replace("/app");
      }
    } catch {
      /* malformed URL — ignore */
    }
  }, [user]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: plansRes }, { data: flagsRes }] = await Promise.all([
          api.get("/billing/plans-public"),
          api.get("/public/feature-flags"),
        ]);
        if (!alive) return;
        setPlans(plansRes?.plans || null);
        setFlags(flagsRes?.flags || {});
      } catch { /* fall back to defaults */ }
    })();
    return () => { alive = false; };
  }, []);
  const findPrice = (id, fallback) => {
    const p = plans?.find((x) => x.id === id);
    if (!p) return fallback;
    if (p.monthly_price === 0) return "$0";
    // Preserve decimals when they matter — $9.99 must NOT render as "$10"
    // in the hero (users complained: "I set $9.99, homepage says $10").
    const n = Number(p.monthly_price);
    const hasCents = Math.abs(n - Math.round(n)) > 0.005;
    return hasCents ? `$${n.toFixed(2)}` : `$${n.toFixed(0)}`;
  };
  // Cheapest paid plan — drives the hero "From $X/mo" line so changing
  // billing prices in /app/admin instantly reflows the landing copy.
  const cheapestPaidPrice = (() => {
    if (!plans?.length) return "$9";
    const paid = plans
      .filter((p) => Number(p.monthly_price) > 0)
      .map((p) => Number(p.monthly_price));
    if (!paid.length) return "$9";
    const min = Math.min(...paid);
    const hasCents = Math.abs(min - Math.round(min)) > 0.005;
    return hasCents ? `$${min.toFixed(2)}` : `$${min.toFixed(0)}`;
  })();
  const isHidden = (key) => flags[key] === "hidden";
  const isComingSoon = (key) => flags[key] === "coming_soon";
  return (
    <div className="min-h-screen bg-[#FFFBF5] text-zinc-950">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-amber-100" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Logo size="md" />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-700">
            <a href="#features" className="hover:text-orange-600">{t("nav_features")}</a>
            <a href="#how" className="hover:text-orange-600">{t("nav_howit")}</a>
            <a href="#download" className="hover:text-orange-600 inline-flex items-center gap-1.5">
              {t("nav_download") || "Download"}
              <span className="inline-flex items-center justify-center text-[8px] font-black uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">New</span>
            </a>
            <a href="#pricing" className="hover:text-orange-600">{t("nav_pricing")}</a>
            <Link to="/support" className="hover:text-orange-600" data-testid="nav-support-link">
              {t("nav_support") || "Support"}
            </Link>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Install Lumioos PWA button — desktop only. On mobile, install
                is offered via the PWA install prompt + the floating button
                at bottom-right of the page, so the cramped top bar stays
                clean. */}
            <div className="hidden md:block">
              <PWAInstallButton variant="inline" />
            </div>
            <LangToggle />
            <Link to="/auth" data-testid="nav-login-btn" className="hidden sm:inline-flex text-sm font-medium text-zinc-700 hover:text-zinc-950 px-3 py-2">
              {t("nav_signin")}
            </Link>
            <Link to="/auth?mode=register" data-testid="nav-signup-btn" className="btn-accent inline-flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2 rounded-md whitespace-nowrap">
              {t("nav_get_started")} <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* Prominent trial ribbon — sits directly under the site header so it's
          the FIRST thing visitors see. Auto-hides when admin disables trial
          via /api/billing/trial-config. */}
      <TrialRibbon />

      {/* Hero with Lumioo glow */}
      <section className="relative overflow-hidden border-b border-amber-100">
        <div className="absolute inset-0 lumioo-glow pointer-events-none" />
        <div className="absolute -top-40 -right-32 w-[600px] h-[600px] lumioo-orb rounded-full pointer-events-none" />
        <div className="absolute inset-0 dotted-bg opacity-30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-in-up">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-200 bg-white/90 backdrop-blur rounded-full text-xs font-semibold uppercase tracking-[0.18em] text-orange-700" data-testid="hero-badge">
                <Sparkles className="w-3.5 h-3.5 text-orange-500" />
                {t("hero_badge")}
              </div>
              <FreeTrialBadge size="md" />
            </div>
            <h1 className="mt-6 text-5xl sm:text-7xl font-black tracking-tighter leading-[0.95]" data-testid="hero-heading">
              <T k="hero_h1_a" />{" "}
              <span className="gradient-text"><T k="hero_h1_b" /></span>.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-zinc-700 max-w-2xl leading-relaxed"><T k="hero_sub" vars={{ from_price: cheapestPaidPrice }} /></p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 flex-wrap">
              <TrialPrimaryCTA fallbackLabel={t("hero_cta_primary")} />
              <a href="#features" data-testid="hero-cta-secondary" className="btn-outline inline-flex items-center justify-center gap-2 text-sm font-semibold px-7 py-4 rounded-md">
                <T k="hero_cta_secondary" />
              </a>
              <PWAInstallButton variant="inline" />
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-zinc-700">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> <T k="trust_no_card" /></div>
              <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-emerald-600" /> <T k="trust_stripe" /></div>
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-600" /> <T k="trust_privacy" /></div>
              <div className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-500 fill-amber-500" /> <T k="trust_rating" /></div>
            </div>
          </div>

          {/* Hero visual: glowing orb showcasing the AI */}
          <div className="lg:col-span-5 fade-in-up" style={{ animationDelay: "120ms" }}>
            <div className="relative aspect-square w-full max-w-md mx-auto">
              {/* Halo */}
              <div className="absolute inset-0 rounded-full lumioo-orb scale-90" />
              {/* Center card */}
              <div className="relative w-full h-full flex items-center justify-center">
                <div className="bg-white/95 backdrop-blur border border-amber-200 rounded-2xl shadow-2xl p-7 w-full max-w-sm">
                  <div className="flex items-center justify-between">
                    <Logo size="sm" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-200">{t("hero_card_live")}</span>
                  </div>
                  <div className="mt-5 text-xs uppercase tracking-[0.18em] text-orange-600 font-bold">{t("hero_card_eyebrow")}</div>
                  <div className="mt-1 text-lg font-bold text-zinc-900 leading-snug">{t("hero_card_question")}</div>
                  <div className="mt-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-lg p-3 text-sm text-zinc-700 leading-relaxed">
                    <span className="font-mono text-[10px] text-orange-600 block mb-1">{t("hero_card_assistant")}</span>
                    {t("hero_card_answer")}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-[11px] text-zinc-500 font-medium">
                    <span className="font-mono">
                      {[
                        !isHidden("chat_smart_model") && "Claude 4.6",
                        !isHidden("gpt5_smart_router") && "GPT-5",
                        !isHidden("opus_master") && "Opus 4.5",
                        !isHidden("chat_quick_model") && "Gemini 3",
                      ].filter(Boolean).join(" · ")}
                    </span>
                    <span className="flex items-center gap-1 text-emerald-600 font-bold"><Check className="w-3 h-3" /> {t("hero_card_time")}</span>
                  </div>
                </div>
              </div>

              {/* Floating chips */}
              <div className="absolute -top-4 -left-4 bg-white border border-amber-200 rounded-lg shadow-lg p-2.5 flex items-center gap-2 text-xs font-semibold text-zinc-700">
                <Mic className="w-4 h-4 text-orange-500" /> {t("hero_chip_voice")}
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white border border-amber-200 rounded-lg shadow-lg p-2.5 flex items-center gap-2 text-xs font-semibold text-zinc-700">
                <ImageIcon className="w-4 h-4 text-pink-500" /> {t("hero_chip_image")}
              </div>
              <div className="absolute top-1/2 -right-8 -translate-y-1/2 bg-white border border-amber-200 rounded-lg shadow-lg p-2.5 flex items-center gap-2 text-xs font-semibold text-zinc-700">
                <Globe className="w-4 h-4 text-amber-500" /> {t("hero_chip_lang")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof / Stats strip */}
      <section className="bg-white border-b border-amber-100">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { k: t("stat_1_n"), v: t("stat_1_v") },
            { k: t("stat_2_n"), v: t("stat_2_v") },
            { k: t("stat_3_n"), v: t("stat_3_v") },
            { k: t("stat_4_n"), v: t("stat_4_v") },
          ].map((s, i) => (
            <div key={i}>
              <div className="text-3xl sm:text-4xl font-black tracking-tighter bg-gradient-to-br from-amber-500 via-orange-500 to-pink-500 bg-clip-text text-transparent">{s.v}</div>
              <div className="mt-1 text-xs font-semibold uppercase tracking-widest text-zinc-500">{s.k}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Brand video demo */}
      {!isHidden("landing_cinematic_video") && <VideoDemo />}

      {/* GPT-5 Smart Router — Pro plan exclusive promo */}
      {!isHidden("gpt5_smart_router") && (
      <section className="border-b border-amber-100 bg-gradient-to-br from-zinc-950 via-violet-950 to-zinc-950 text-white relative overflow-hidden" data-testid="smart-router-promo">
        {/* Background glow */}
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-600 rounded-full blur-[140px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-fuchsia-600 rounded-full blur-[140px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-lg shadow-violet-500/30">
                <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse" />
                {t("router_promo_badge") || "NEW · PRO EXCLUSIVE"}
              </div>
              <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
                <span className="bg-gradient-to-r from-white via-violet-200 to-fuchsia-200 bg-clip-text text-transparent">GPT-5 Smart Router</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-zinc-300 leading-relaxed max-w-xl">
                {t("router_promo_desc") || "The most powerful model on the planet — automatically routed to your hardest tasks. One toggle in chat sends complex code, analysis, and reasoning to OpenAI GPT-5, while everyday questions stay snappy on Claude 4.5. No model picker. No friction."}
              </p>
              <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-lg">
                {[
                  { icon: "⚡", text: t("router_promo_bullet1") || "Auto-selects GPT-5 for hard tasks" },
                  { icon: "🧠", text: t("router_promo_bullet2") || "World-leading reasoning + code" },
                  { icon: "🚀", text: t("router_promo_bullet3") || "Priority queue, no rate limits" },
                  { icon: "🎯", text: t("router_promo_bullet4") || "One-click toggle in chat" },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm text-zinc-200">
                    <span className="text-lg">{f.icon}</span>
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/pricing" data-testid="smart-router-upgrade-cta"
                  className="inline-flex items-center gap-2 bg-white text-zinc-900 hover:bg-zinc-100 px-6 py-3 rounded-full text-sm font-bold transition-all hover:scale-105 shadow-xl shadow-violet-500/20">
                  {t("router_promo_cta") || "Upgrade to Pro"} <ArrowRight className="w-4 h-4" />
                </Link>
                <span className="text-xs text-zinc-400 font-mono">{t("router_promo_included") || "Included free with every Pro plan"}</span>
              </div>
            </div>

            {/* Visual: mock chat showing the toggle */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-br from-violet-500/50 to-fuchsia-500/50 rounded-2xl blur-2xl" aria-hidden="true" />
                <div className="relative bg-zinc-900/90 border border-white/10 rounded-2xl p-5 backdrop-blur-sm shadow-2xl">
                  <div className="flex items-center justify-between pb-3 border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Lumioos · Chat</div>
                  </div>
                  <div className="mt-4 space-y-3 min-h-[140px]">
                    <div className="text-xs text-zinc-400 font-mono">You</div>
                    <div className="bg-zinc-800/80 border border-white/5 rounded-lg p-3 text-sm text-zinc-200">
                      Refactor this React component to use hooks + add full TypeScript types.
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white px-2.5 py-1 rounded-full font-bold text-[10px] uppercase tracking-wider shadow-md shadow-violet-500/40">
                        ⚡ GPT-5
                      </span>
                      <span className="text-zinc-500 font-mono text-[10px]">Smart-routed · 1.4s</span>
                    </div>
                  </div>
                  {/* Toolbar mock */}
                  <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-1.5">
                    <div className="px-2.5 py-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-[10px] font-bold rounded-md inline-flex items-center gap-1 shadow-md">
                      ⚡ GPT-5
                    </div>
                    <div className="px-2.5 py-1.5 bg-zinc-800 text-zinc-400 text-[10px] font-semibold rounded-md">Image</div>
                    <div className="px-2.5 py-1.5 bg-zinc-800 text-zinc-400 text-[10px] font-semibold rounded-md">Code</div>
                    <div className="px-2.5 py-1.5 bg-zinc-800 text-zinc-400 text-[10px] font-semibold rounded-md">CV</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* GPT 5.6 Family — Sol / Terra / Luna — Pro & Power exclusive promo */}
      {(!isHidden("gpt56_sol") || !isHidden("gpt56_terra") || !isHidden("gpt56_luna")) && (
      <section className="border-b border-zinc-800 bg-gradient-to-br from-slate-950 via-zinc-950 to-slate-900 text-white relative overflow-hidden" data-testid="gpt56-promo">
        {/* Cosmic background — Sol/Terra/Luna feels like a solar system */}
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute top-10 left-[10%] w-96 h-96 bg-orange-500 rounded-full blur-[140px]" />
          <div className="absolute top-1/3 right-[10%] w-96 h-96 bg-teal-500 rounded-full blur-[140px]" />
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-blue-500 rounded-full blur-[140px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-20 sm:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur border border-white/20 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-lg">
              <span className="inline-block w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              {t("gpt56_promo_badge") || "NEW · PRO & POWER EXCLUSIVE"}
            </div>
            <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
              <span className="bg-gradient-to-r from-orange-300 via-teal-300 to-blue-300 bg-clip-text text-transparent">
                {t("gpt56_promo_title") || "GPT 5.6 Family"}
              </span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-zinc-300 leading-relaxed">
              {(() => {
                const raw = t("gpt56_promo_desc") || "Meet the next generation of GPT — three flavors, one purpose: give you the exact balance of depth, breadth, and speed you need for every task. Available today on {plans} plans.";
                const plans = t("gpt56_promo_plans_hl") || "Pro & Power";
                const parts = raw.split("{plans}");
                return parts.length === 2
                  ? (<>{parts[0]}<strong className="text-white">{plans}</strong>{parts[1]}</>)
                  : raw;
              })()}
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                key: "sol",
                icon: "🌞",
                name: "GPT 5.6 Sol",
                tag: t("gpt56_sol_tag") || "Deepest reasoning",
                desc: t("gpt56_sol_desc") || "For the hardest problems — long analyses, research, complex code review. When accuracy matters more than speed.",
                gradient: "from-orange-500 via-rose-500 to-red-600",
                border: "border-orange-400/40",
                shadow: "shadow-orange-500/30",
              },
              {
                key: "terra",
                icon: "🌍",
                name: "GPT 5.6 Terra",
                tag: t("gpt56_terra_tag") || "Balanced power",
                desc: t("gpt56_terra_desc") || "Your everyday workhorse. Great answers, fast enough, wide knowledge — the sweet spot between Sol and Luna.",
                gradient: "from-emerald-500 via-teal-500 to-cyan-600",
                border: "border-teal-400/40",
                shadow: "shadow-teal-500/30",
              },
              {
                key: "luna",
                icon: "🌙",
                name: "GPT 5.6 Luna",
                tag: t("gpt56_luna_tag") || "Fastest 5.6",
                desc: t("gpt56_luna_desc") || "Snappy replies for chat, brainstorming, and quick lookups. Still smarter than most models — just faster.",
                gradient: "from-indigo-500 via-blue-500 to-sky-600",
                border: "border-blue-400/40",
                shadow: "shadow-blue-500/30",
              },
            ].filter((c) => !isHidden(`gpt56_${c.key}`)).map((c) => (
              <div key={c.key}
                className={`relative rounded-2xl p-6 bg-zinc-900/60 backdrop-blur border ${c.border} shadow-2xl ${c.shadow} hover:scale-[1.02] transition-transform`}>
                <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-gradient-to-r ${c.gradient} text-white text-[11px] font-black uppercase tracking-wider shadow-lg`}>
                  <span>{c.icon}</span> {c.name}
                </div>
                <div className="mt-4 text-xl font-bold text-white">{c.tag}</div>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-zinc-900 font-bold text-sm hover:bg-zinc-100 transition-colors shadow-lg">
              {t("gpt56_cta_plans") || "See Pro & Power plans →"}
            </Link>
            <Link to="/auth?mode=register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-white/30 text-white font-bold text-sm hover:bg-white/10 transition-colors">
              {t("gpt56_cta_trial") || "Start 14-day free trial"}
            </Link>
          </div>
        </div>
      </section>
      )}

      {!isHidden("chat_smart_model") && (
      <section className="border-b border-amber-100 bg-white" data-testid="best-ai-section">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">
              {t("bestai_eyebrow") || "Smart Default"}
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter">
              {t("bestai_h2") || "Powered by Claude 4.5."}
            </h2>
            <p className="mt-4 text-lg text-zinc-600">
              {t("bestai_sub") || "Currently #1 for writing, code and reasoning. Switch to GPT-5 or Gemini 3 anytime — no extra cost."}
            </p>
          </div>

          {/* Smart default highlight card */}
          <div className="mt-10 bg-gradient-to-br from-orange-50 via-amber-50 to-pink-50 border border-amber-200 rounded-2xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full blur-3xl opacity-20" />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 bg-white border border-orange-300 px-3 py-1.5 rounded-full">
                  <Sparkles className="w-3 h-3" /> {t("sonnet_promo_pill") || "Best for general tasks"}
                </div>
                <h3 className="mt-4 text-3xl font-black tracking-tighter">
                  Claude Sonnet 4.5 <span className="text-orange-600">{t("sonnet_promo_default_label") || "— Lumioos default"}</span>
                </h3>
                <p className="mt-3 text-zinc-700 max-w-xl">
                  {t("sonnet_promo_desc") || "Anthropic's flagship model leads independent benchmarks for writing, code, and complex reasoning. Lower hallucination rate than GPT-5 and stronger multilingual quality. Set as Lumioos default so new users get the best results out of the box."}
                </p>
              </div>
              <div className="flex md:flex-col gap-2 md:gap-3 text-center">
                <div className="px-5 py-3 bg-white border border-amber-200 rounded-lg">
                  <div className="text-2xl font-black tracking-tighter text-orange-600">#1</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-0.5">{t("sonnet_col_writing") || "Writing"}</div>
                </div>
                <div className="px-5 py-3 bg-white border border-amber-200 rounded-lg">
                  <div className="text-2xl font-black tracking-tighter text-orange-600">#1</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-0.5">{t("sonnet_col_code") || "Code"}</div>
                </div>
                <div className="px-5 py-3 bg-white border border-amber-200 rounded-lg">
                  <div className="text-2xl font-black tracking-tighter text-orange-600">#1</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-0.5">{t("sonnet_col_reasoning") || "Reasoning"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Best AI per task table */}
          <div className="mt-12">
            <h3 className="text-2xl font-black tracking-tighter text-center">
              {t("bestai_table_h") || "Pick the right model for the job."}
            </h3>
            <p className="text-center text-zinc-600 mt-2 max-w-xl mx-auto">
              {t("bestai_table_sub") || "Lumioos gives you all three — switch with one click per chat."}
            </p>

            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { task: t("bestai_task_writing")   || "Long-form writing",     best: "Claude 4.6",   flag: "chat_smart_model", icon: "✍️", color: "from-orange-500 to-amber-500" },
                { task: t("bestai_task_code")      || "Code & debug",          best: "Claude 4.6",   flag: "chat_smart_model", icon: "💻", color: "from-orange-500 to-amber-500" },
                { task: t("bestai_task_translate") || "Translate (TR/AR/RU)",  best: "Claude 4.6",   flag: "chat_smart_model", icon: "🌍", color: "from-orange-500 to-amber-500" },
                { task: t("bestai_task_reasoning") || "Hardest reasoning",     best: "GPT-5",        flag: "gpt5_smart_router", icon: "👑", color: "from-amber-500 to-yellow-500" },
                { task: t("bestai_task_capable")   || "Most capable",          best: "Opus 4.5",     flag: "opus_master",       icon: "✨", color: "from-violet-500 to-fuchsia-500" },
                { task: t("bestai_task_quick")     || "Quick answers",         best: "Gemini 3 Flash", flag: "chat_quick_model", icon: "⚡", color: "from-sky-500 to-blue-500" },
              ].filter((row) => !isHidden(row.flag)).map((row, i) => (
                <div
                  key={i}
                  className="bg-white border border-amber-200 rounded-xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  data-testid={`best-ai-row-${i}`}
                >
                  <div className="text-2xl mb-2">{row.icon}</div>
                  <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                    {row.task}
                  </div>
                  <div className={`mt-1 text-lg font-black tracking-tight bg-gradient-to-r ${row.color} bg-clip-text text-transparent`}>
                    {row.best}
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-6 text-center text-xs text-zinc-500 max-w-2xl mx-auto">
              {t("bestai_footnote") || "All models included in your Lumioos subscription — switch with one click, no extra cost."}
            </p>
          </div>
        </div>
      </section>
      )}

      {/* Features */}
      <section id="features" className="border-b border-amber-100">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">{t("feat_section_eyebrow")}</div>
            <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter">{t("feat_section_h2")}</h2>
            <p className="mt-4 text-lg text-zinc-600">{t("feat_section_sub")}</p>
          </div>

          <div className={`mt-16 grid grid-cols-1 gap-px bg-amber-100 border border-amber-100 ${isHidden("landing_video_producer_promo") ? "" : "lg:grid-cols-2"}`}>
            <div className="bg-white p-10 hover-lift" data-testid="feature-chat">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <h3 className="mt-6 text-2xl font-bold tracking-tight">{t("feat_chat_title")}</h3>
              <p className="mt-3 text-zinc-600 leading-relaxed">{t("feat_chat_desc")}</p>
              <ul className="mt-6 space-y-2 text-sm text-zinc-700">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("feat_chat_li1")}</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("feat_chat_li2")}</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("feat_chat_li3")}</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("feat_chat_li4")}</li>
              </ul>
            </div>

            {!isHidden("landing_video_producer_promo") && (
            <div className="bg-white p-10 hover-lift relative" data-testid="feature-video-producer">
              <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-100 border border-amber-200 px-2 py-1 rounded-full">
                {isComingSoon("video_producer") ? "Coming soon" : "Coming soon"}
              </span>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                <Clapperboard className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <h3 className="mt-6 text-2xl font-bold tracking-tight">{t("feat_video_title")}</h3>
              <p className="mt-3 text-zinc-600 leading-relaxed">{t("feat_video_desc")}</p>
              <ul className="mt-6 space-y-2 text-sm text-zinc-700">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("feat_video_li1")}</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("feat_video_li2")}</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("feat_video_li3")}</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("feat_video_li4")}</li>
              </ul>
            </div>
            )}
          </div>

          {/* Interview Practice — wide hero card */}
          <div className="mt-px grid grid-cols-1 bg-amber-100 border border-t-0 border-amber-100">
            <div className="bg-gradient-to-br from-zinc-900 via-zinc-950 to-black text-white p-10 hover-lift relative overflow-hidden" data-testid="feature-interview">
              <div className="absolute -right-10 -top-10 w-64 h-64 rounded-full bg-orange-500/20 blur-3xl pointer-events-none" />
              <div className="absolute -left-16 -bottom-16 w-72 h-72 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />
              <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                <div className="md:col-span-2">
                  <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded">
                    <Sparkles className="w-3 h-3" /> {t("feat_iv_eyebrow")}
                  </div>
                  <h3 className="mt-4 text-3xl sm:text-4xl font-black tracking-tighter">{t("feat_iv_title")}</h3>
                  <p className="mt-3 text-zinc-300 leading-relaxed max-w-2xl">{t("feat_iv_desc")}</p>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-zinc-200">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> {t("feat_iv_li1")}</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> {t("feat_iv_li2")}</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> {t("feat_iv_li3")}</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> {t("feat_iv_li4")}</li>
                  </ul>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Link
                      to="/auth?mode=register"
                      className="btn-accent inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-md"
                      data-testid="cta-try-interview"
                    >
                      {t("feat_iv_cta")} <ArrowRight className="w-4 h-4" />
                    </Link>
                    <span className="text-xs text-zinc-400">{t("feat_iv_use_cases")}</span>
                  </div>
                </div>
                <div className="hidden md:flex justify-center items-center">
                  <div className="relative w-44 h-44 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-orange-700 flex items-center justify-center shadow-2xl shadow-orange-900/40">
                    <div className="absolute inset-2 rounded-full bg-zinc-900/90 flex items-center justify-center">
                      <GraduationCap className="w-16 h-16 text-amber-300" strokeWidth={1.5} />
                    </div>
                    <span className="absolute -bottom-2 -right-2 text-xs font-bold bg-amber-300 text-zinc-900 px-2.5 py-1 rounded-full shadow-md">{t("feat_iv_badge")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-px grid grid-cols-1 md:grid-cols-3 gap-px bg-amber-100 border border-t-0 border-amber-100">
            {[
              { icon: Zap, title: t("feat_speed_t"), desc: t("feat_speed_d") },
              { icon: Shield, title: t("feat_secure_t"), desc: t("feat_secure_d") },
              { icon: Globe, title: t("feat_global_t"), desc: t("feat_global_d") },
            ].map((f, i) => (
              <div key={i} className="bg-white p-8">
                <f.icon className="w-6 h-6 text-orange-500" strokeWidth={1.8} />
                <h4 className="mt-4 text-lg font-bold tracking-tight">{f.title}</h4>
                <p className="mt-1 text-sm text-zinc-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Game Studio advert — wrapped in feature-flag check. When the
          admin sets `game_studio = hidden` in feature_flags, this entire
          section disappears from the public landing page.
          When state = `coming_soon` we keep the promo visible but flip
          the badge / CTA / overlay so users see it's not yet bookable. */}
      {!isHidden("game_studio") && (() => {
        const gsSoon = isComingSoon("game_studio");
        return (
      <section className="border-b border-amber-100 bg-gradient-to-br from-indigo-50 via-violet-50 to-orange-50 overflow-hidden" data-testid="landing-game-studio-ad">
        <div className="max-w-7xl mx-auto px-6 py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
              gsSoon
                ? "bg-amber-100 border border-amber-300 text-amber-900"
                : "bg-violet-100 border border-violet-300 text-violet-900"
            }`} data-testid="gs-ad-badge">
              {gsSoon ? `🚧 ${t("coming_soon_badge") || "Coming soon"} · Game Studio` : "🎮 New · Game Studio"}
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter">
              {t("gs_ad_h2") || (<>Imagine a game.<br/><span className="bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">Lumioos builds it.</span></>)}
            </h2>
            <p className="mt-4 text-zinc-700 text-lg max-w-xl">
              {t("gs_ad_sub") || "Type 'a cat catching fish under a pixel-art moon' — get a playable HTML game in 15 seconds. Plays on phone, desktop, anywhere. Download the .html and keep it forever."}
            </p>
            <ul className="mt-6 space-y-2 text-zinc-700 text-sm">
              <li>✅ {t("gs_ad_b1") || "Generate from a single sentence — any style, any mechanic"}</li>
              <li>✅ {t("gs_ad_b2") || "Download as a single .html file — no installs, no servers"}</li>
              <li>✅ {t("gs_ad_b3") || "Touch + keyboard controls baked in — works on every device"}</li>
              <li>✅ {t("gs_ad_b4") || "Creator $9 → 7 credits/mo · Pro $29 → 25 · Power $79 → 80"}</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              {gsSoon ? (
                <>
                  <a
                    href="#pricing"
                    data-testid="gs-ad-cta-soon"
                    className="inline-flex items-center gap-2 text-sm font-bold px-5 py-3 rounded-md bg-amber-500 hover:bg-amber-600 text-white shadow-sm"
                  >
                    {t("gs_ad_cta_soon") || "Get notified at launch"}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <span className="inline-flex items-center text-sm font-semibold px-3 py-3 text-zinc-500">
                    🔒 {t("gs_ad_locked") || "Locked for now — admin will flip the switch."}
                  </span>
                </>
              ) : (
                <>
                  <Link to="/auth?mode=register" data-testid="gs-ad-cta" className="btn-accent inline-flex items-center gap-2 text-sm font-bold px-5 py-3 rounded-md">
                    {t("gs_ad_cta") || "Start free trial — build your first game"}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href="#pricing" className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 rounded-md border border-zinc-300 hover:border-zinc-500 text-zinc-700">
                    {t("gs_ad_see_plans") || "See pricing"}
                  </a>
                </>
              )}
            </div>
          </div>
          <div className="relative">
            <div className={`rounded-2xl bg-zinc-900 shadow-2xl overflow-hidden border-4 border-zinc-800 ${gsSoon ? "opacity-90" : ""}`}>
              <div className="px-4 py-2.5 bg-zinc-800 flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                <div className="ml-3 text-zinc-400 text-xs font-mono">lumioos/game-studio</div>
              </div>
              <div className="p-6 sm:p-8 text-center select-none">
                <div className="text-6xl sm:text-7xl mb-3 animate-pulse">🐈⬛ 🐟</div>
                <div className="text-zinc-400 text-xs mb-2 font-mono">Score: 1240 · Lives: ❤️❤️❤️</div>
                <div className="text-2xl sm:text-3xl font-black text-orange-400 tracking-tight">PURRR-FECT CATCH!</div>
                <div className="mt-4 inline-block px-3 py-1 rounded-full bg-zinc-800 text-zinc-300 text-xs font-mono">Made with Lumioos · 12 KB · 0 deps</div>
              </div>
            </div>
            {gsSoon ? (
              <div className="hidden sm:block absolute -bottom-3 -right-3 px-3 py-1.5 rounded-full bg-amber-500 text-white text-xs font-bold shadow-lg rotate-3">🚧 launching soon</div>
            ) : (
              <div className="hidden sm:block absolute -bottom-3 -right-3 px-3 py-1.5 rounded-full bg-amber-400 text-amber-950 text-xs font-bold shadow-lg rotate-3">⚡ ~15s · generated by AI</div>
            )}
          </div>
        </div>
      </section>
        );
      })()}

      {/* How it works */}
      <section id="how" className="border-b border-amber-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">{t("how_eyebrow")}</div>
          <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter max-w-2xl">{t("how_h2")}</h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: t("how_s1_t"), desc: t("how_s1_d") },
              { step: "02", title: t("how_s2_t"), desc: t("how_s2_d") },
              { step: "03", title: t("how_s3_t"), desc: t("how_s3_d") },
            ].map((s, i) => (
              <div key={i} className="border border-amber-100 p-8 hover-lift bg-gradient-to-br from-white to-amber-50/30">
                <div className="text-5xl font-black tracking-tighter bg-gradient-to-br from-amber-500 to-pink-500 bg-clip-text text-transparent">{s.step}</div>
                <h4 className="mt-6 text-xl font-bold tracking-tight">{s.title}</h4>
                <p className="mt-2 text-zinc-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download — Microsoft Store live, Apple/Android coming soon */}
      <DownloadSection flags={flags} />

      {/* Pricing — matches real plans Free / Creator $5 / Pro $10 */}
      <section id="pricing" className="border-b border-amber-100 bg-[#FFFBF5]">        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">{t("lp_pricing_eyebrow")}</div>
            <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter">{t("lp_pricing_h2")}</h2>
            <p className="mt-4 text-lg text-zinc-600">{t("lp_pricing_sub")}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(() => {
              // Video Producer behaviour mirrors the Billing page:
              //   hidden      → drop the line entirely
              //   coming_soon → show muted with a "Soon" badge
              //   enabled     → show normally
              const videoHidden = isHidden("video_producer");
              const videoSoon = isComingSoon("video_producer");
              const creatorVideoLine = `${t("feat_video_title")} · 5/${t("pricing_month") || "mo"}`;
              const proVideoLine = `${t("feat_video_title")} · 15/${t("pricing_month") || "mo"} (HD)`;
              const powerVideoLine = `${t("feat_video_title")} · 50/${t("pricing_month") || "mo"} (HD long-form)`;
              return [
              { id: "free", name: "Free", icon: "🌱", price: findPrice("free", "$0"), desc: t("lp_plan_free_desc"),
                features: [
                  { line: t("lp_plan_free_f1") },
                  { line: t("lp_plan_free_f2") },
                  { line: t("lp_plan_free_f3") },
                ],
                models: [
                  ...(!isHidden("chat_quick_model") ? [{ name: "Gemini 3 Flash", soon: isComingSoon("chat_quick_model") }] : []),
                ] },
              { id: "creator", name: "Creator", icon: "💎", price: findPrice("creator", "$9"), desc: t("lp_plan_creator_desc"),
                features: [
                  { line: t("lp_plan_creator_f1") },
                  { line: t("lp_plan_creator_f2") },
                  { line: t("lp_plan_creator_f3") },
                  { line: t("lp_plan_creator_f4") },
                  ...(videoHidden ? [] : [{ line: creatorVideoLine, comingSoon: videoSoon }]),
                ],
                models: [
                  ...(!isHidden("chat_quick_model") ? [{ name: "Gemini 3 Flash", soon: isComingSoon("chat_quick_model") }] : []),
                  ...(!isHidden("chat_smart_model") ? [{ name: "Claude Sonnet 4.6", soon: isComingSoon("chat_smart_model") }] : []),
                ] },
              { id: "pro", name: "Pro", icon: "👑", price: findPrice("pro", "$29"), desc: t("lp_plan_pro_desc"),
                features: [
                  { line: t("lp_plan_pro_f1") },
                  { line: t("lp_plan_pro_f2") },
                  { line: t("lp_plan_pro_f3") },
                  { line: t("lp_plan_pro_f4") },
                  ...(videoHidden ? [] : [{ line: proVideoLine, comingSoon: videoSoon }]),
                  ...(isHidden("cv_chat") ? [] : [{ line: t("lp_plan_pro_f5"), comingSoon: isComingSoon("cv_chat") }]),
                ],
                models: [
                  ...(!isHidden("chat_quick_model") ? [{ name: "Gemini 3 Flash", soon: isComingSoon("chat_quick_model") }] : []),
                  ...(!isHidden("chat_smart_model") ? [{ name: "Claude Sonnet 4.6", soon: isComingSoon("chat_smart_model") }] : []),
                  ...(!isHidden("gpt5_smart_router") ? [{ name: "GPT-5", soon: isComingSoon("gpt5_smart_router") }] : []),
                  ...(!isHidden("opus_master") ? [{ name: "Opus 4.5", soon: isComingSoon("opus_master") }] : []),
                  ...(!isHidden("gpt56_sol")   ? [{ name: "GPT 5.6 Sol",   soon: isComingSoon("gpt56_sol") }] : []),
                  ...(!isHidden("gpt56_terra") ? [{ name: "GPT 5.6 Terra", soon: isComingSoon("gpt56_terra") }] : []),
                  ...(!isHidden("gpt56_luna")  ? [{ name: "GPT 5.6 Luna",  soon: isComingSoon("gpt56_luna") }] : []),
                ] },
              { id: "power", name: "Power", icon: "⚡", price: findPrice("power", "$79"), desc: t("lp_plan_power_desc"),
                features: [
                  { line: t("lp_plan_power_f1") },
                  { line: t("lp_plan_power_f2") },
                  { line: t("lp_plan_power_f3") },
                  { line: t("lp_plan_power_f4") },
                  ...(videoHidden ? [] : [{ line: powerVideoLine, comingSoon: videoSoon }]),
                  ...(isHidden("cv_chat") ? [] : [{ line: t("lp_plan_power_f5"), comingSoon: isComingSoon("cv_chat") }]),
                ],
                models: [
                  ...(!isHidden("chat_quick_model") ? [{ name: "Gemini 3 Flash", soon: isComingSoon("chat_quick_model") }] : []),
                  ...(!isHidden("chat_smart_model") ? [{ name: "Claude Sonnet 4.6", soon: isComingSoon("chat_smart_model") }] : []),
                  ...(!isHidden("gpt5_smart_router") ? [{ name: "GPT-5", soon: isComingSoon("gpt5_smart_router") }] : []),
                  ...(!isHidden("opus_master") ? [{ name: "Opus 4.5", soon: isComingSoon("opus_master") }] : []),
                  ...(!isHidden("gpt56_sol")   ? [{ name: "GPT 5.6 Sol",   soon: isComingSoon("gpt56_sol") }] : []),
                  ...(!isHidden("gpt56_terra") ? [{ name: "GPT 5.6 Terra", soon: isComingSoon("gpt56_terra") }] : []),
                  ...(!isHidden("gpt56_luna")  ? [{ name: "GPT 5.6 Luna",  soon: isComingSoon("gpt56_luna") }] : []),
                ] },
            ];})().map((p) => {
              const isCreator = p.id === "creator";
              const isPro = p.id === "pro";
              const isPower = p.id === "power";
              const themeClasses = isPower
                ? "bg-gradient-to-br from-purple-50 via-fuchsia-50 to-purple-100/70 border-2 border-purple-400 shadow-2xl shadow-purple-300/50 ring-1 ring-purple-300/40"
                : isPro
                ? "bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100/60 border-2 border-amber-400 shadow-2xl shadow-amber-200/50 ring-1 ring-amber-300/40"
                : isCreator
                ? "bg-gradient-to-br from-zinc-50 via-slate-50 to-zinc-100 border-2 border-zinc-400 shadow-xl shadow-zinc-200/40 ring-1 ring-zinc-300/40"
                : "bg-white border-2 border-amber-100 hover:border-amber-300 transition-colors";
              const ribbonClasses = isPower
                ? "bg-gradient-to-r from-purple-600 via-fuchsia-600 to-purple-700 text-white"
                : isPro
                ? "bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-zinc-900"
                : isCreator
                ? "bg-gradient-to-r from-zinc-400 via-slate-300 to-zinc-500 text-zinc-900"
                : "";
              const eyebrowColor = isPower ? "text-purple-700" : isPro ? "text-amber-700" : isCreator ? "text-zinc-600" : "text-orange-600";
              return (
              <div key={p.id}
                className={`relative p-8 rounded-xl ${themeClasses}`}
                data-testid={`lp-pricing-${p.id}`}>
                {(isPro || isCreator || isPower) && (
                  <div className={`absolute -top-3 left-8 px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded-full shadow-md ${ribbonClasses}`}>
                    {isPower ? "⚡ Power" : isPro ? "★ Pro" : "Creator"}
                  </div>
                )}
                <div className={`text-xs uppercase tracking-[0.2em] font-bold ${eyebrowColor} flex items-center gap-1.5`}>
                  <span className="text-base">{p.icon}</span> {p.name}
                </div>
                <div className="mt-4 text-5xl font-black tracking-tighter">{p.price}<span className="text-base font-normal text-zinc-500">/{t("pricing_month")}</span></div>
                <div className="mt-2 text-sm text-zinc-700">{p.desc}</div>
                <ul className="mt-6 space-y-2.5 text-sm">
                  {p.features.map((f, i) => (
                    <li key={i} className={`flex items-start gap-2 ${f.comingSoon ? "text-zinc-400" : ""}`}>
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${
                        f.comingSoon
                          ? "text-zinc-300"
                          : isPower ? "text-purple-600" : isPro ? "text-amber-600" : isCreator ? "text-zinc-600" : "text-emerald-600"
                      }`} />
                      <span className={`flex-1 ${f.comingSoon ? "text-zinc-400" : "text-zinc-700"}`}>{f.line}</span>
                      {f.comingSoon && (
                        <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600 shrink-0">
                          Soon
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {/* AI Models included */}
                <div className="mt-6 pt-5 border-t border-zinc-200/60">
                  <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${eyebrowColor} mb-2.5`}>
                    AI Models Included
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.models.map((m, i) => {
                      const isGpt5 = m.name === "GPT-5";
                      const isOpus = m.name === "Opus 4.5";
                      const isSol = m.name === "GPT 5.6 Sol";
                      const isTerra = m.name === "GPT 5.6 Terra";
                      const isLuna = m.name === "GPT 5.6 Luna";
                      return (
                        <span key={i}
                          className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded ${
                            isOpus
                              ? "bg-gradient-to-r from-violet-500 via-fuchsia-500 to-purple-600 text-white shadow-sm shadow-fuchsia-300/50"
                              : isGpt5
                              ? "bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-zinc-900 shadow-sm shadow-amber-300/50"
                              : isSol
                              ? "bg-gradient-to-r from-orange-400 via-rose-400 to-red-500 text-white shadow-sm shadow-orange-300/50"
                              : isTerra
                              ? "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-sm shadow-teal-300/50"
                              : isLuna
                              ? "bg-gradient-to-r from-indigo-500 via-blue-500 to-sky-600 text-white shadow-sm shadow-blue-300/50"
                              : "bg-white border border-zinc-300 text-zinc-700"
                          } ${m.soon ? "opacity-60" : ""}`}>
                          {isOpus ? "✨ " : isGpt5 ? "👑 " : isSol ? "🌞 " : isTerra ? "🌍 " : isLuna ? "🌙 " : ""}{m.name}
                          {m.soon && (
                            <span className="ml-0.5 text-[8.5px] font-black uppercase tracking-wider px-1 py-0.5 rounded bg-zinc-200 text-zinc-600">
                              Soon
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <Link to="/auth?mode=register"
                  className={`mt-7 w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-5 py-3 rounded-md transition-all ${
                    isPower
                      ? "bg-gradient-to-r from-purple-600 via-fuchsia-600 to-purple-700 hover:from-purple-700 hover:via-fuchsia-700 hover:to-purple-800 text-white shadow-lg shadow-purple-500/40"
                      : isPro
                      ? "bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-700 text-zinc-900 shadow-lg shadow-amber-500/30"
                      : isCreator
                      ? "bg-gradient-to-r from-zinc-700 via-zinc-600 to-zinc-700 hover:from-zinc-800 hover:via-zinc-700 hover:to-zinc-800 text-white"
                      : "bg-zinc-950 text-white hover:bg-zinc-800"
                  }`}>
                  {t("lp_plan_start")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            );})}
          </div>

          <div className="mt-8 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-zinc-600">
            <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-emerald-600" /> {t("lp_pricing_trust1")}</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {t("lp_pricing_trust2")}</div>
            <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-600" /> {t("lp_pricing_trust3")}</div>
          </div>

        </div>
      </section>

      {/* Final CTA */}
      <section className="border-b border-amber-100">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center relative">
          <div className="absolute inset-0 lumioo-glow opacity-60 pointer-events-none" />
          <div className="relative">
            <h2 className="text-4xl sm:text-5xl font-black tracking-tighter">{t("cta_h2_a")} <span className="gradient-text">{t("cta_h2_b")}</span></h2>
            <p className="mt-4 text-lg text-zinc-600 max-w-xl mx-auto">{t("cta_sub")}</p>
            <Link to="/auth?mode=register" className="btn-accent inline-flex items-center justify-center gap-2 text-sm font-bold px-7 py-4 rounded-md mt-8">
              {t("cta_btn")} <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="mt-4 text-xs text-zinc-500">{t("cta_smallprint")}</div>
          </div>
        </div>
      </section>

      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size="sm" />
          <div className="flex items-center gap-4 text-sm text-zinc-500" data-testid="footer-attribution">
            <Link to="/support" className="hover:text-zinc-900 transition-colors" data-testid="footer-support-link">
              {t("footer_support") || "Support"}
            </Link>
            <Link to="/privacy" className="hover:text-zinc-900 transition-colors" data-testid="footer-privacy-link">
              {t("footer_privacy") || "Privacy"}
            </Link>
            <Link to="/terms" className="hover:text-zinc-900 transition-colors" data-testid="footer-terms-link">
              {t("footer_terms") || "Terms"}
            </Link>
            <span className="hidden sm:inline">·</span>
            <span>© {new Date().getFullYear()}</span>
            <span className="font-bold bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 bg-clip-text text-transparent">Lumioos by Alber Behar</span>
          </div>
        </div>
      </footer>

      {/* Floating PWA install prompt */}
      <PWAInstallButton variant="floating" />
    </div>
  );
}
